import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SearchIcon, BellIcon, ChevronDownIcon, SettingsIcon, MenuIcon, CheckIcon, FileTextIcon, AlertCircleIcon, UserPlusIcon, CpuIcon, CalendarIcon, CopyIcon, LogOutIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile, useIsTablet } from '../../hooks/useMediaQuery';
import { useRole, Role } from '../../hooks/useRole';
interface TopBarProps {
  onOpenMobileSidebar: () => void;
  onToggleTabletSidebar: () => void;
  isTabletSidebarOpen: boolean;
  topBarVisible: boolean;
}
// Role-aware notifications
const superadminNotifications = [{
  id: 1,
  title: 'Errore comunicazione macchinario',
  desc: 'Impossibile comunicare con "Carestream DRX". Verificare la connessione.',
  time: '10 min fa',
  read: false,
  icon: CpuIcon,
  color: 'text-status-error',
  bg: 'bg-status-error-bg'
}, {
  id: 2,
  title: 'Macchinario disconnesso',
  desc: '"Hitachi Oasis" risulta disconnesso da oltre 24 ore.',
  time: '1 ora fa',
  read: false,
  icon: AlertCircleIcon,
  color: 'text-status-warning',
  bg: 'bg-status-warning-bg'
}, {
  id: 3,
  title: 'Nuovo paziente registrato',
  desc: "Elena Colombo ha completato l'onboarding.",
  time: '2 ore fa',
  read: true,
  icon: UserPlusIcon,
  color: 'text-blue-600',
  bg: 'bg-blue-50'
}, {
  id: 4,
  title: 'Import prenotazioni completato',
  desc: 'Sincronizzate 15 nuove prenotazioni dal CUP.',
  time: 'Ieri',
  read: true,
  icon: CalendarIcon,
  color: 'text-medical-green',
  bg: 'bg-medical-green-50'
}, {
  id: 5,
  title: 'Aggiornamento configurazione',
  desc: 'Nuova integrazione HL7 configurata con successo.',
  time: 'Ieri',
  read: true,
  icon: CopyIcon,
  color: 'text-text-secondary',
  bg: 'bg-clinical-gray-100'
}];
const medicoNotifications = [{
  id: 1,
  title: 'Nuovo esame assegnato',
  desc: 'Risonanza magnetica encefalo per Marco Rossi assegnata.',
  time: '10 min fa',
  read: false,
  icon: FileTextIcon,
  color: 'text-medical-green',
  bg: 'bg-medical-green-50'
}, {
  id: 2,
  title: 'Referto inviato con successo',
  desc: 'Il referto per Anna Ferrari è stato consegnato al paziente.',
  time: '1 ora fa',
  read: false,
  icon: FileTextIcon,
  color: 'text-medical-green',
  bg: 'bg-medical-green-50'
}, {
  id: 3,
  title: 'Errore invio referto',
  desc: 'Impossibile inviare email a Giulia Bianchi. Riprovare.',
  time: '2 ore fa',
  read: true,
  icon: AlertCircleIcon,
  color: 'text-status-error',
  bg: 'bg-status-error-bg'
}, {
  id: 4,
  title: 'Referto salvato automaticamente',
  desc: 'Bozza del referto per Luca Romano salvata.',
  time: 'Ieri',
  read: true,
  icon: FileTextIcon,
  color: 'text-text-secondary',
  bg: 'bg-clinical-gray-100'
}];
const pazienteNotifications = [{
  id: 1,
  title: 'Il tuo referto è disponibile',
  desc: 'Il referto della risonanza magnetica encefalo è pronto per il download.',
  time: '10 min fa',
  read: false,
  icon: FileTextIcon,
  color: 'text-medical-green',
  bg: 'bg-medical-green-50'
}, {
  id: 2,
  title: 'Il tuo esame è stato programmato',
  desc: 'TAC torace prevista per il 15 Mar 2026 alle 10:30.',
  time: '1 ora fa',
  read: false,
  icon: CalendarIcon,
  color: 'text-blue-600',
  bg: 'bg-blue-50'
}, {
  id: 3,
  title: 'Il tuo referto è stato inviato nuovamente',
  desc: 'Il referto della TAC torace è stato reinviato alla tua email.',
  time: 'Ieri',
  read: true,
  icon: FileTextIcon,
  color: 'text-medical-green',
  bg: 'bg-medical-green-50'
}];
function getNotificationsForRole(role: Role) {
  switch (role) {
    case 'paziente':
      return pazienteNotifications;
    case 'medico':
      return medicoNotifications;
    default:
      return superadminNotifications;
  }
}
// Mock Search Results
const mockSearchResults = [{
  type: 'Paziente',
  name: 'Marco Rossi',
  sub: 'CF: RSSMRC80A01H501Z',
  path: '/patients/PAT-001'
}, {
  type: 'Paziente',
  name: 'Giulia Bianchi',
  sub: 'CF: BNCGLI85B42F205X',
  path: '/patients/PAT-002'
}, {
  type: 'Esame',
  name: 'Risonanza magnetica encefalo',
  sub: 'Marco Rossi - 11 Mar 2026',
  path: '/exams'
}, {
  type: 'Referto',
  name: 'Referto RM Encefalo',
  sub: 'In corso - Dr. S. Verdi',
  path: '/exams/report/EXM-5001'
}, {
  type: 'Modello',
  name: 'Referto RM Encefalo Standard',
  sub: 'Standard globale',
  path: '/templates/TPL-001'
}, {
  type: 'Macchinario',
  name: 'Siemens Magnetom',
  sub: 'RM - Connesso',
  path: '/machines'
}];
export function TopBar({
  onOpenMobileSidebar,
  onToggleTabletSidebar,
  isTabletSidebarOpen,
  topBarVisible
}: TopBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const {
    role,
    setRole
  } = useRole();
  const [notifications, setNotifications] = useState(() => getNotificationsForRole(role));
  const searchRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const unreadCount = notifications.filter(n => !n.read).length;
  // Update notifications when role changes
  useEffect(() => {
    setNotifications(getNotificationsForRole(role));
  }, [role]);
  // Close dropdowns on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const filteredSearch = searchQuery.trim() === '' ? [] : mockSearchResults.filter(r => r.name.toLowerCase().includes(searchQuery.toLowerCase()) || r.type.toLowerCase().includes(searchQuery.toLowerCase()) || r.sub.toLowerCase().includes(searchQuery.toLowerCase()));
  const markAsRead = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setNotifications(prev => prev.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
  };
  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({
      ...n,
      read: true
    })));
  };
  const handleNotificationClick = (n: any) => {
    setNotifications(prev => prev.map(notif => notif.id === n.id ? {
      ...notif,
      read: true
    } : notif));
    setIsNotificationsOpen(false);
  };
  // Determine left offset based on viewport
  const leftOffset = isMobile || isTablet ? 'left-0' : 'left-[260px]';
  return <header className={`
        h-16 bg-white border-b border-clinical-gray-100 fixed top-0 right-0 z-20 flex items-center justify-between px-4 md:px-8
        transition-all duration-300 ease-in-out
        ${leftOffset}
        ${isTablet && !topBarVisible ? '-translate-y-full' : 'translate-y-0'}
      `} data-id="element-131">
      {/* Left section: Hamburger (mobile/tablet) + Search */}
      <div className="flex items-center gap-4 flex-1" data-id="element-132">
        {/* Mobile hamburger */}
        {isMobile && <button onClick={onOpenMobileSidebar} className="p-2 -ml-2 text-text-secondary hover:bg-clinical-gray rounded-full" aria-label="Apri menu" data-id="element-133">
            <MenuIcon size={24} data-id="element-134" />
          </button>}

        {/* Tablet hamburger */}
        {isTablet && <button onClick={onToggleTabletSidebar} className="p-2 -ml-2 text-text-secondary hover:bg-clinical-gray rounded-full transition-colors" aria-label={isTabletSidebarOpen ? 'Chiudi menu' : 'Apri menu'} data-id="element-135">
            <MenuIcon size={24} data-id="element-136" />
          </button>}

        <div ref={searchRef} className={`relative ${isMobile ? 'w-full max-w-[200px]' : 'w-64 lg:w-96'}`} data-id="element-137">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-138">
            <SearchIcon size={18} className="text-text-tertiary" data-id="element-139" />
          </div>
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => setIsSearchFocused(true)} placeholder={isMobile ? 'Cerca...' : isTablet ? 'Cerca...' : 'Cerca pazienti, esami o prenotazioni...'} className="medical-input pl-10 py-2 bg-clinical-gray-50 border-transparent focus:bg-white text-sm" data-id="element-140" />

          {/* Search Dropdown */}
          <AnimatePresence data-id="element-141">
            {isSearchFocused && searchQuery.trim() !== '' && <motion.div initial={{
            opacity: 0,
            y: 5
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 5
          }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-medical-lg shadow-modal border border-clinical-gray-100 max-h-96 overflow-y-auto z-50 py-2" data-id="element-142">
                {filteredSearch.length > 0 ? filteredSearch.map((result, idx) => <div key={idx} onClick={() => {
              navigate(result.path);
              setIsSearchFocused(false);
              setSearchQuery('');
            }} className="px-4 py-2.5 hover:bg-clinical-gray-50 cursor-pointer border-b border-clinical-gray-50 last:border-0" data-id="element-143">
                      <div className="flex justify-between items-start mb-0.5" data-id="element-144">
                        <span className="text-sm font-medium text-text-primary" data-id="element-145">
                          {result.name}
                        </span>
                        <span className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary bg-clinical-gray-100 px-1.5 py-0.5 rounded" data-id="element-146">
                          {result.type}
                        </span>
                      </div>
                      <span className="text-xs text-text-secondary" data-id="element-147">
                        {result.sub}
                      </span>
                    </div>) : <div className="px-4 py-6 text-center text-sm text-text-secondary" data-id="element-148">
                    Nessun risultato trovato per "{searchQuery}"
                  </div>}
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-2 md:gap-4 shrink-0" data-id="element-149">
        {/* Role Switcher — desktop only (on tablet it's in sidebar) */}
        {!isMobile && !isTablet && <div className="flex items-center bg-clinical-gray-100 p-1 rounded-lg border border-clinical-gray-200 mr-2" data-id="element-150">
            <button onClick={() => {
          setRole('superadmin');
          navigate('/');
        }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${role === 'superadmin' ? 'bg-white shadow-sm text-text-primary' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-151">
              Superadmin
            </button>
            <button onClick={() => {
          setRole('medico');
          navigate('/');
        }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${role === 'medico' ? 'bg-white shadow-sm text-text-primary' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-152">
              Medico
            </button>
            <button onClick={() => {
          setRole('paziente');
          navigate('/patient-reports');
        }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${role === 'paziente' ? 'bg-white shadow-sm text-text-primary' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-153">
              Paziente
            </button>
          </div>}

        {/* Notifications — always visible */}
        <div className="relative" ref={notifRef} data-id="element-154">
          <button onClick={() => setIsNotificationsOpen(!isNotificationsOpen)} className="relative p-2 text-text-secondary hover:text-text-primary hover:bg-clinical-gray rounded-full transition-colors" aria-label="Notifiche" data-id="element-155">
            <BellIcon size={20} data-id="element-156" />
            {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-status-error rounded-full border-2 border-white" data-id="element-157"></span>}
          </button>

          <AnimatePresence data-id="element-158">
            {isNotificationsOpen && <motion.div initial={{
            opacity: 0,
            y: 10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 10,
            scale: 0.95
          }} transition={{
            duration: 0.15
          }} className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-medical-lg shadow-modal border border-clinical-gray-100 z-50 flex flex-col max-h-[80vh]" data-id="element-159">
                <div className="px-4 py-3 border-b border-clinical-gray-100 flex items-center justify-between shrink-0" data-id="element-160">
                  <h3 className="text-sm font-semibold text-text-primary" data-id="element-161">
                    Notifiche
                  </h3>
                  {unreadCount > 0 && <button onClick={markAllRead} className="text-xs text-medical-green hover:underline" data-id="element-162">
                      Segna tutte come lette
                    </button>}
                </div>

                <div className="overflow-y-auto flex-1 py-1" data-id="element-163">
                  {notifications.length > 0 ? notifications.map(n => <div key={n.id} onClick={() => handleNotificationClick(n)} className={`px-4 py-3 hover:bg-clinical-gray-50 cursor-pointer flex gap-3 border-b border-clinical-gray-50 last:border-0 transition-colors ${!n.read ? 'bg-medical-green-50/30' : ''}`} data-id="element-164">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${n.bg} ${n.color}`} data-id="element-165">
                          <n.icon size={16} data-id="element-166" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-167">
                          <div className="flex justify-between items-start mb-0.5" data-id="element-168">
                            <p className={`text-sm truncate pr-2 ${!n.read ? 'font-semibold text-text-primary' : 'font-medium text-text-secondary'}`} data-id="element-169">
                              {n.title}
                            </p>
                            <span className="text-[10px] text-text-tertiary whitespace-nowrap shrink-0" data-id="element-170">
                              {n.time}
                            </span>
                          </div>
                          <p className="text-xs text-text-secondary line-clamp-2" data-id="element-171">
                            {n.desc}
                          </p>
                        </div>
                        {!n.read && <button onClick={e => markAsRead(n.id, e)} className="shrink-0 w-5 h-5 rounded-full hover:bg-clinical-gray-200 flex items-center justify-center text-text-tertiary hover:text-medical-green transition-colors mt-1" title="Segna come letta" data-id="element-172">
                            <CheckIcon size={12} data-id="element-173" />
                          </button>}
                      </div>) : <div className="px-4 py-8 text-center text-sm text-text-secondary" data-id="element-174">
                      Nessuna notifica
                    </div>}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Profile — desktop and mobile only (on tablet it is in sidebar) */}
        {!isTablet && <div className="relative" data-id="element-175">
            <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="flex items-center gap-2 hover:bg-clinical-gray py-1 px-1 md:px-2 rounded-md transition-colors min-h-[44px]" data-id="element-176">
              <div className="w-8 h-8 rounded-full bg-medical-green text-white flex items-center justify-center text-sm font-medium shrink-0" data-id="element-177">
                {role === 'superadmin' ? 'SA' : role === 'medico' ? 'MD' : 'PZ'}
              </div>
              {!isMobile && <>
                  <span className="text-sm font-medium text-text-primary hidden sm:block capitalize" data-id="element-178">
                    {role}
                  </span>
                  <ChevronDownIcon size={16} className="text-text-tertiary hidden sm:block" data-id="element-179" />
                </>}
            </button>

            <AnimatePresence data-id="element-180">
              {isProfileOpen && <>
                  <div className="fixed inset-0 z-10" onClick={() => setIsProfileOpen(false)} data-id="element-181" />
                  <motion.div initial={{
              opacity: 0,
              y: 10,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: 10,
              scale: 0.95
            }} transition={{
              duration: 0.15
            }} className="absolute right-0 mt-2 w-48 bg-white rounded-medical shadow-modal border border-clinical-gray-100 z-20 py-1" data-id="element-182">
                    {role !== 'paziente' && <button onClick={() => {
                navigate('/settings');
                setIsProfileOpen(false);
              }} className="w-full text-left px-4 py-2.5 text-sm text-text-primary hover:bg-clinical-gray flex items-center gap-2 min-h-[44px]" data-id="element-183">
                        <SettingsIcon size={16} className="text-text-secondary" data-id="element-184" />{' '}
                        Impostazioni
                      </button>}
                    <button onClick={() => {
                setIsProfileOpen(false);
              }} className="w-full text-left px-4 py-2.5 text-sm text-status-error hover:bg-status-error-bg flex items-center gap-2 min-h-[44px] transition-colors" data-id="element-185">
                      <LogOutIcon size={16} data-id="element-186" /> Log-out
                    </button>
                  </motion.div>
                </>}
            </AnimatePresence>
          </div>}
      </div>
    </header>;
}